import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import ProfilePage from "./pages/ProfilePage";
import FavoritesPage from "./pages/FavoritesPage";
import AppliedPage from "./pages/AppliedPage";
import AdminPage from "./pages/AdminPage";
import MessagesPage from "./pages/MessagesPage";
import ClientLogin from "./pages/ClientLogin";
import ClientPortal from "./pages/ClientPortal";

// Protected Route Component
function ProtectedRoute({ children }) {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    // If user data passed from AuthCallback, render immediately
    if (location.state?.user) {
        return children;
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-void flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground font-mono text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
}

// Public Route - redirect to dashboard if authenticated
function PublicRoute({ children }) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-void flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground font-mono text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

// Router with session_id detection
function AppRouter() {
    const location = useLocation();

    // Check URL fragment for session_id - process FIRST before any route
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route 
                path="/" 
                element={
                    <PublicRoute>
                        <LandingPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <ProfilePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/favorites" 
                element={
                    <ProtectedRoute>
                        <FavoritesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/applied" 
                element={
                    <ProtectedRoute>
                        <AppliedPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/messages" 
                element={
                    <ProtectedRoute>
                        <MessagesPage />
                    </ProtectedRoute>
                } 
            />
            {/* Client Portal Routes (separate auth) */}
            <Route path="/client/login" element={<ClientLogin />} />
            <Route path="/client/portal" element={<ClientPortal />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App dark">
            <AuthProvider>
                <BrowserRouter>
                    <AppRouter />
                </BrowserRouter>
                <Toaster 
                    position="top-right"
                    toastOptions={{
                        className: 'bg-void-paper border-border text-white font-mono text-sm',
                    }}
                />
            </AuthProvider>
        </div>
    );
}

export default App;
